import React from "react";

import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";

/** Three horizontal lines + right-pointing triangle (play).
 *  Used for: Episodes nav item, Episodes stat card. */
export function EpisodesIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <line
        x1="3"
        y1="6"
        x2="14"
        y2="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="3"
        y1="12"
        x2="14"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="3"
        y1="18"
        x2="10"
        y2="18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <polygon points="17,7.5 22,12 17,16.5" fill="currentColor" />
    </SvgIcon>
  );
}

/** Upward arrow rising from a horizontal base line.
 *  Used for: Upload Episode nav item. */
export function UploadEpisodeIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Base line */}
      <line
        x1="3"
        y1="20"
        x2="21"
        y2="20"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Vertical stem */}
      <line
        x1="12"
        y1="17"
        x2="12"
        y2="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Arrow head */}
      <polyline
        points="7,11 12,6 17,11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </SvgIcon>
  );
}

/** Open folder outline.
 *  Used for: Projects nav item. */
export function ProjectsFolderIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Two small document lines inside folder */}
      <line
        x1="8"
        y1="13"
        x2="16"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      <line
        x1="8"
        y1="17"
        x2="13"
        y2="17"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </SvgIcon>
  );
}

/** Three circles connected by lines (share / distribute network).
 *  Used for: Shared Episodes nav item, Shared stat card. */
export function ShareNetworkIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Top circle */}
      <circle
        cx="18"
        cy="5"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      {/* Bottom-left circle */}
      <circle
        cx="6"
        cy="12"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      {/* Bottom-right circle */}
      <circle
        cx="18"
        cy="19"
        r="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      {/* Line top → left */}
      <line
        x1="8.4"
        y1="10.7"
        x2="15.6"
        y2="6.3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Line left → bottom */}
      <line
        x1="8.4"
        y1="13.3"
        x2="15.6"
        y2="17.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </SvgIcon>
  );
}

/** Circular clock face with hour + minute hands.
 *  Used for: Duration stat, Total Minutes stat card. */
export function ClockIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      {/* Minute hand (pointing to ~12:00 position, i.e. straight up) */}
      <line
        x1="12"
        y1="12"
        x2="12"
        y2="6.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Hour hand (pointing to ~3 o'clock) */}
      <line
        x1="12"
        y1="12"
        x2="16.5"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Center dot */}
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </SvgIcon>
  );
}

/** Calendar page with a date line.
 *  Used for: Uploaded stat. */
export function CalendarIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      <line
        x1="3"
        y1="9"
        x2="21"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <line
        x1="8"
        y1="2"
        x2="8"
        y2="6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <line
        x1="16"
        y1="2"
        x2="16"
        y2="6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Date lines */}
      <line
        x1="7"
        y1="14"
        x2="17"
        y2="14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="18"
        x2="13"
        y2="18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </SvgIcon>
  );
}

/** Document with horizontal text lines.
 *  Used for: Words stat. */
export function DocumentTextIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <polyline
        points="14,2 14,8 20,8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <line
        x1="8"
        y1="13"
        x2="16"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="8"
        y1="17"
        x2="13"
        y2="17"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </SvgIcon>
  );
}

/** Three vertical bars (bar chart).
 *  Used for: Shorts stat. */
export function BarChartIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Base line */}
      <line
        x1="3"
        y1="20"
        x2="21"
        y2="20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      {/* Bar 1 (short) */}
      <rect x="4" y="14" width="4" height="6" rx="1" fill="currentColor" />
      {/* Bar 2 (tall) */}
      <rect x="10" y="7" width="4" height="13" rx="1" fill="currentColor" />
      {/* Bar 3 (medium) */}
      <rect x="16" y="10" width="4" height="10" rx="1" fill="currentColor" />
    </SvgIcon>
  );
}

/** Users / people icon.
 *  Used for: Users nav item. */
export function UsersIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Back person */}
      <circle
        cx="16"
        cy="8"
        r="3"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      <path
        d="M21 20a5 5 0 0 0-5-5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
      {/* Front person */}
      <circle
        cx="9"
        cy="8"
        r="3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        fill="none"
      />
      <path
        d="M3 21a7 7 0 0 1 12 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
    </SvgIcon>
  );
}
